import { useMsal } from "@azure/msal-react";

export interface CurrentUser {
  email: string;
  name: string;
  initials: string;
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

// Current-admin email from the signed `amy-preview-access` session cookie. This is the unified
// preview/local/cypress identity: real engineers get it via OIDC, Cypress mints it via
// `cy.loginAsAdmin(email)`. The backend independently HMAC-verifies the same cookie
// (HubbleAuthenticationFilter) — the client only reads the email portion for display/role gating,
// it doesn't (and can't) verify the signature. Only consulted when auth is disabled / signed-cookie
// mode, so it has no effect in a real MSAL session.
//
// Note: the e2e cookie Cypress sets is readable here (not HttpOnly); the human grant sets an
// HttpOnly cookie and will resolve identity via a /apollo/whoami endpoint instead (ENG-5576).
const SESSION_COOKIE = "amy-preview-access";

function readSessionEmail(): string | undefined {
  try {
    const cookie = globalThis.document?.cookie ?? "";
    const match = cookie.match(new RegExp(`(?:^|; )${SESSION_COOKIE}=([^;]*)`));
    if (!match?.[1]) return undefined;
    // Cookie value is `<email>|<exp>|<sig>`; the email is the first segment.
    const value = decodeURIComponent(match[1]);
    const email = value.split("|")[0];
    return email && email.length > 0 ? email : undefined;
  } catch {
    // document.cookie access can throw (sandboxed iframe) — ignore.
    return undefined;
  }
}

// Derives a display name from an email prefix (e.g. "jane.doe@…" → "Jane Doe").
function nameFromEmail(email: string): string {
  const prefix = email.split("@")[0] ?? email;
  return prefix
    .split(/[._-]/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
}

export function useCurrentUser(
  authDisabled?: boolean,
): CurrentUser | undefined {
  const { instance } = useMsal();

  if (authDisabled) {
    const sessionEmail = readSessionEmail();
    if (sessionEmail) {
      const name = nameFromEmail(sessionEmail);
      return { email: sessionEmail, name, initials: getInitials(name) };
    }
    return {
      email: "dev@freightsimple.com",
      name: "Dev Mode",
      initials: "DM",
    };
  }

  const activeAccount = instance.getActiveAccount();
  if (!activeAccount) return undefined;

  const email = activeAccount.username ?? "";
  const name = activeAccount.name ?? email;
  return { email, name, initials: getInitials(name) };
}
